import { Link } from 'react-router-dom'
import type {
  CompanyDashboardMember,
  CompanyDashboardMemberStatus,
} from '@/types/company.types'
import { Badge, Panel } from '@/components/ui'
import type { BadgeVariant } from '@/components/ui'
import { Avatar } from '@/components/common/Avatar'
import { cn } from '@/lib/cn'

export interface MemberRosterPanelProps {
  companyId: string
  members: CompanyDashboardMember[]
  memberCount: number
  timezone: string
  isLoading: boolean
}

interface StatusPresentation {
  label: string
  variant: BadgeVariant
}

/**
 * `null` is deliberately not "Absent". Absent means the system recorded a miss
 * against a scheduled shift; null means nothing was scheduled or recorded, and
 * calling that absent would misreport the data.
 */
const STATUS: Record<CompanyDashboardMemberStatus, StatusPresentation> = {
  present: { label: 'Present', variant: 'success' },
  late: { label: 'Late', variant: 'warning' },
  absent: { label: 'Absent', variant: 'danger' },
  'half-day': { label: 'Half day', variant: 'info' },
  'on-leave': { label: 'On leave', variant: 'info' },
}

const NO_RECORD: StatusPresentation = { label: 'No shift today', variant: 'neutral' }

/** Clock-in rendered in the company's timezone, not the viewer's. */
function formatClockIn(iso: string, timezone: string): string | null {
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone || undefined,
    }).format(new Date(iso))
  } catch {
    return null
  }
}

function MemberRow({
  member,
  timezone,
}: {
  member: CompanyDashboardMember
  timezone: string
}): React.ReactElement {
  const presentation = member.status ? STATUS[member.status] : NO_RECORD
  const clockIn = member.clockIn ? formatClockIn(member.clockIn, timezone) : null

  return (
    <li className="flex items-center gap-3 px-4 py-2.5">
      <Avatar name={member.name} size="md" />

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">
          {member.name}
        </span>
        <span className="block truncate text-xs capitalize text-gray-500 dark:text-gray-400">
          {member.role}
        </span>
      </span>

      {clockIn && (
        <span className="hidden shrink-0 text-xs tabular-nums text-gray-500 sm:block dark:text-gray-400">
          {member.isClockedIn ? 'in since' : 'from'} {clockIn}
        </span>
      )}

      {member.isClockedIn && (
        <span
          className="h-1.5 w-1.5 shrink-0 rounded-full bg-teal-500 dark:bg-teal-400"
          title="Currently clocked in"
          aria-label="Currently clocked in"
        />
      )}

      <Badge variant={presentation.variant} size="sm" className="shrink-0">
        {presentation.label}
      </Badge>
    </li>
  )
}

/**
 * The page's subject: who is on this company, and where they stand today.
 *
 * Sorted server-side (open sessions first, then by how much the row needs a
 * human). Scrolls internally past ~8 rows so page height stays bounded no
 * matter how large the roster gets.
 */
export function MemberRosterPanel({
  companyId,
  members,
  memberCount,
  timezone,
  isLoading,
}: MemberRosterPanelProps): React.ReactElement {
  const overflow = memberCount - members.length

  return (
    <Panel
      as="section"
      aria-labelledby="roster-heading"
      title={
        <div className="flex items-baseline gap-2">
          <h3
            id="roster-heading"
            className="text-sm font-semibold tracking-[-0.006em] text-gray-900 dark:text-gray-100"
          >
            Members
          </h3>
          {!isLoading && (
            <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400">
              {memberCount}
            </span>
          )}
        </div>
      }
      action={
        <Link
          to={`/members?companyId=${companyId}`}
          className="text-xs font-medium text-primary underline-offset-4 hover:underline dark:text-sky-400"
        >
          Manage
        </Link>
      }
      dense
      contentClassName="p-1.5"
    >
      {isLoading ? (
        <ul className="space-y-1 p-1" aria-hidden>
          {[0, 1, 2].map((i) => (
            <li key={i} className="flex items-center gap-3 px-3 py-2.5">
              <span className="h-8 w-8 shrink-0 rounded-full bg-gray-100 dark:bg-gray-700" />
              <span className="h-3.5 flex-1 rounded bg-gray-100 dark:bg-gray-700" />
              <span className="h-5 w-16 rounded-full bg-gray-100 dark:bg-gray-700" />
            </li>
          ))}
          <span className="sr-only">Loading members</span>
        </ul>
      ) : members.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">No members yet</p>
          <p className="mx-auto mt-1 max-w-xs text-xs text-gray-500 dark:text-gray-400">
            Add people to this company to start tracking attendance, shifts and payroll.
          </p>
          <Link
            to={`/companies/${companyId}/setup-wizard#import`}
            className="mt-3 inline-block text-xs font-medium text-primary underline-offset-4 hover:underline dark:text-sky-400"
          >
            Add members
          </Link>
        </div>
      ) : (
        <>
          <ul
            className={cn(
              'divide-y divide-gray-100 dark:divide-gray-700/60',
              members.length > 8 && 'thin-scroll max-h-[26rem] overflow-y-auto'
            )}
          >
            {members.map((member) => (
              <MemberRow key={member.userId} member={member} timezone={timezone} />
            ))}
          </ul>
          {overflow > 0 && (
            <p className="px-4 py-2.5 text-xs text-gray-500 dark:text-gray-400">
              Showing {members.length} of {memberCount}.{' '}
              <Link
                to={`/members?companyId=${companyId}`}
                className="font-medium text-primary underline-offset-4 hover:underline dark:text-sky-400"
              >
                View all
              </Link>
            </p>
          )}
        </>
      )}
    </Panel>
  )
}

MemberRosterPanel.displayName = 'MemberRosterPanel'
